import { isNil } from 'lodash';
import request, { AxiosResponseType } from '../../request';
import {
  ListResource,
  Agreement,
  ResourceAgreement,
  LatentCharge,
  ChargeCreation,
  ResourceAgreementCharge,
  PreviewCalculationResource,
  ChargeProviders,
  AgreementCharge,
  SignatureType,
  Recipient,
  GeofenceAlertTypeResource,
} from 'services/resources';

/**
 * Returns the agreements list
 */
export function query(limit: number, offset: number, filters: URLSearchParams, search: string) {
  const params = {
    limit: limit.toString(),
    offset: offset.toString(),
    search,
  };
  const urlParams = new URLSearchParams(params);

  for (const [key, val] of filters.entries()) {
    urlParams.append(key, val);
  }

  return request<ListResource<ResourceAgreement>>('fleet/agreements/', {
    method: 'GET',
    params: urlParams,
  });
}

/**
 * Returns an agreement with the given id
 */
export function find(id: number) {
  return request<ResourceAgreement>(`fleet/agreements/${id}/`, {
    method: 'GET',
  });
}

/**
 * Creates a new Agreement
 */
export function create(agreement: Partial<Agreement>, actingAs: number | null) {
  // TODO: improve typing with required fields

  // only add acting_as param if provided
  const params = new URLSearchParams();
  if (actingAs) {
    params.append('acting_as', String(actingAs));
  }

  return request<ResourceAgreement>('fleet/agreements/', {
    method: 'POST',
    data: agreement,
    params,
  });
}

/**
 * Updates an agreement with the given id
 */
export function update(id: number, agreement: Agreement) {
  return request<ResourceAgreement>(`fleet/agreements/${id}/`, {
    method: 'PUT',
    data: agreement,
  });
}

/**
 * Patches an agreement with the given id
 */
export function patch(id: number, agreement: Partial<Agreement>) {
  return request<ResourceAgreement>(`fleet/agreements/${id}/`, {
    method: 'PATCH',
    data: agreement,
  });
}

/**
 * Sign an agreement with the given id
 */
export function sign(id: number, data: { customerSignature: string; signatureType?: SignatureType }) {
  return request(`fleet/agreements/${id}/sign/`, {
    method: 'POST',
    data,
  });
}

/**
 * Deletes an agreement with the given id
 */
export function destroy(id: number) {
  return request(`fleet/agreements/${id}/`, {
    method: 'DELETE',
  });
}

export function requestUpdateAgreementCharges(agreementId: number, charges) {
  return request(`fleet/agreements/${agreementId}/charges/`, {
    method: 'PUT',
    data: charges,
  });
}

export function requestDeleteAgreementCharges(agreementId: number, chargeIds: number[]) {
  return request(`fleet/agreements/${agreementId}/charges/`, {
    method: 'DELETE',
    data: chargeIds,
  });
}

/**
 * Adds a new note for the customer
 */
export function createVehicleInspectionNote(id, note) {
  return request(`fleet/inspections/${id}/notes/`, {
    method: 'POST',
    data: {
      inspection: id,
      note,
    },
  });
}

export function requestCreateAgreementCharge(agreementId: number, charge: ChargeCreation) {
  return request<ResourceAgreementCharge>(`fleet/agreements/${agreementId}/charges/`, {
    method: 'POST',
    data: charge,
  });
}

export function requestDeleteAgreementCharge(agreementId: number, chargeId: number) {
  return request(`fleet/agreements/${agreementId}/charges/${chargeId}/`, {
    method: 'DELETE',
  });
}

export function requestListAgreementCharges(agreementId: number) {
  return request<ResourceAgreementCharge>(`fleet/agreements/${agreementId}/charges/`, {
    method: 'GET',
  });
}

export function requestGetAgreementCharge(agreementId: number, charge: LatentCharge) {
  return request<ResourceAgreementCharge>(`fleet/agreements/${agreementId}/charges/${charge.id}/`, {
    method: 'GET',
  });
}

export function requestUpdateAgreementCharge(agreementId: number, charge: AgreementCharge) {
  return request<ResourceAgreementCharge>(`fleet/agreements/${agreementId}/charges/${charge.id}/`, {
    method: 'PATCH',
    data: charge,
  });
}

export function requestPrintAgreement(id) {
  return request(`fleet/agreements/${id}/export/`, {});
}

export function requestEmailAgreement(id) {
  return request(`fleet/agreements/${id}/email/`, {});
}

export function requestDownloadAgreement(id, autofilledCharges?) {
  return request<Blob, AxiosResponseType.Blob>(`fleet/agreements/${id}/download/`, {
    responseType: AxiosResponseType.Blob,
    method: 'POST',
    data: autofilledCharges,
  });
}

export function requestGetAgreementHTML(id: number, signatureType: SignatureType) {
  const params = new URLSearchParams({ signatureType: signatureType });
  return request<void, AxiosResponseType.Text>(`fleet/agreements/${id}/render_html/`, {
    responseType: AxiosResponseType.Text,
    params,
  });
}

interface CardToken {
  token: string;
  setDefault: boolean;
  agreementId?: number;
  customer?: number;
}

// If no customerID. returns a credit card with no owner defined. If a customerID
// is provided, owner will be customerID and backend will associate card to
// customer AND set the card as default.
export function createCC(
  token: CardToken['token'],
  customerId?: CardToken['customer'],
  agreementId?: CardToken['agreementId'],
  setDefault = false
) {
  let data: CardToken = { token, setDefault };

  if (!isNil(customerId)) {
    data = {
      customer: customerId,
      ...data,
    };
  }
  if (!isNil(agreementId)) {
    data = {
      agreementId: agreementId,
      ...data,
    };
  }

  return request(`fleet/cc/`, {
    method: 'POST',
    data,
  });
}

export function charge(id: number, data: ChargeProviders) {
  return request<ResourceAgreement>(`fleet/agreements/${id}/charge_customer/`, {
    method: 'POST',
    data: data,
  });
}

export function sendCopy(id, recipient) {
  return request(`fleet/agreements/${id}/send/`, {
    method: 'POST',
    data: { ...recipient },
  });
}

export function requestPreviewChargeCalculations(charges, agreementId?) {
  return request<PreviewCalculationResource>(`fleet/agreements/charges/preview/`, {
    method: 'POST',
    data: { charges, agreementId },
  });
}

export function sendCustomerNotification(agreementId) {
  return request(`fleet/agreements/${agreementId}/send_customer_notification/`, {
    method: 'POST',
  });
}

export function markManuallyNotified(agreementId) {
  return request(`fleet/agreements/${agreementId}/mark_manually_notified/`, {
    method: 'POST',
  });
}

export function sendAgreementForSignature(agreementId, recipient) {
  return request(`fleet/agreements/${agreementId}/request_signature/`, {
    method: 'POST',
    data: { ...recipient },
  });
}

/**
 * The following are the contactless-only resources,
 * requiring a contactless_token.
 */

export function contactlessAgreementGet(id: number, contactlessToken: string) {
  if (typeof contactlessToken === 'string' && contactlessToken.length > 0) {
    return request<ResourceAgreement>(`fleet/agreements/${id}/`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${contactlessToken}`,
      },
    });
  } else {
    throw new Error('ContactlessToken not provided...');
  }
}

export function customerSendCopy(id: number, recipient: Recipient, contactlessToken: string) {
  if (typeof contactlessToken === 'string' && contactlessToken.length > 0) {
    return request(`fleet/agreements/${id}/send/`, {
      method: 'POST',
      data: { ...recipient },
      headers: {
        Authorization: `Bearer ${contactlessToken}`,
      },
    });
  } else {
    throw new Error('ContactlessToken not provided...');
  }
}

export function customerSign(
  id: number,
  data: { customerSignature: string; signatureType?: SignatureType },
  contactlessToken: string
) {
  if (typeof contactlessToken === 'string' && contactlessToken.length > 0) {
    return request(`fleet/agreements/${id}/sign/`, {
      method: 'POST',
      data,
      headers: {
        Authorization: `Bearer ${contactlessToken}`,
      },
    });
  } else {
    throw new Error('ContactlessToken not provided...');
  }
}

export function customerGetAgreementHTML(id: number, contactlessToken: string) {
  if (typeof contactlessToken === 'string' && contactlessToken.length > 0) {
    return request(`fleet/agreements/${id}/render_html/`, {
      responseType: AxiosResponseType.Text,
      headers: {
        Authorization: `Bearer ${contactlessToken}`,
      },
    });
  } else {
    throw new Error('ContactlessToken not provided...');
  }
}

export function geofenceAlerts(agreementId: number) {
  return request<GeofenceAlertTypeResource>(`fleet/agreements/${agreementId}/geofence_alerts/`, {
    method: 'GET',
  });
}
